import { toast } from 'sonner';
import { usePreviewPanelStore } from '@/stores/previewPanelStore';
import { OrdreSchema, type Ordre } from '@hector/shared-types';

/**
 * Canonical UUID v1–v5 regex for the jurisprudence-search feature.
 * Duplicated intentionally from the backend sanitizer (C3) — this guard is
 * INDEPENDENT of the backend: ordre/id may originate from an LLM-emitted
 * hector-link tag and must be validated before touching any store state.
 * Defense in depth: never trust LLM-emitted identifiers without front-side validation.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Longueur au-delà de laquelle un libellé d'onglet est coupé — l'onglet lui-même
 *  fait au plus 13rem, donc il est de toute façon tronqué à l'écran ; ce plafond
 *  borne ce qu'on met EN MÉMOIRE et dans l'infobulle. */
const TAB_TITLE_MAX_LENGTH = 80;

/**
 * Nettoie un libellé d'onglet proposé par l'ouvreur.
 *
 * WHY : les deux ouvreurs tirent leur libellé d'un texte écrit par un LLM — la
 * puce `<hector-link type="jp" name="…">` et le `title` d'un résultat de recherche.
 * Rien ne garantit qu'il soit court, sur une seule ligne, ni dépourvu de caractères
 * de contrôle ou de formatage invisibles. C'est le même principe que la validation
 * d'`ordre` et d'`id` juste en dessous : ce service est la frontière où l'on cesse
 * de faire confiance au modèle.
 *
 * Rend `undefined` s'il ne reste rien d'exploitable — l'onglet portera alors son
 * libellé d'attente, jamais une chaîne vide.
 */
export function sanitizeDecisionTabTitle(raw: string | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const cleaned = raw
    // Contrôles C0/C1 et caractères de formatage invisibles (RLO, ZWJ…).
    .replace(/[\p{Cc}\p{Cf}]/gu, ' ')
    // Tout blanc, y compris les sauts de ligne, devient une espace simple.
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return undefined;
  return cleaned.length > TAB_TITLE_MAX_LENGTH
    ? `${cleaned.slice(0, TAB_TITLE_MAX_LENGTH - 1).trimEnd()}…`
    : cleaned;
}

/**
 * Single front entry point to open the internal decision viewer.
 * All callers (hector-link parser, jurisprudence result cards, …) MUST go through
 * this service — never call `usePreviewPanelStore.getState().openDecision()` directly.
 *
 * Depuis le 2026-09-15 l'arrêt s'ouvre comme un ONGLET du panneau d'aperçu, à
 * côté des pièces : coque partagée, pipelines de données séparés. Le texte de la
 * décision continue d'être chargé par `useDecision` dans le corps de l'onglet,
 * jamais par ce service ni par le store.
 */
export const DecisionViewerService = {
  /**
   * Validates `ordre` + `id`, then opens the decision preview store.
   * No-ops silently (with a console.warn) on invalid input.
   *
   * @param ordre - Juridiction type (must satisfy OrdreSchema)
   * @param id    - Decision UUID (must be a valid UUID v1–v5)
   * @param caseId - dossier d'origine, quand l'appelant le connaît : sert à
   *   pré-sélectionner la destination dans « Enregistrer ». Jamais validé ici —
   *   il n'ouvre aucun accès, le dialog le repasse par les endpoints scopés.
   * @param title - libellé d'onglet provisoire, quand l'ouvreur en affiche déjà un.
   *   Assaini ici (cf. `sanitizeDecisionTabTitle`) et remplacé par le vrai titre de
   *   la décision dès son chargement. Sans lui, l'onglet s'ouvre sur « Arrêt… ».
   */
  viewDecision({ ordre, id, caseId, title }: { ordre: Ordre; id: string; caseId?: string; title?: string }): void {
    // Independent front guard: ordre/id can come from an LLM hector-link tag —
    // never open the store on unvalidated input (defense in depth, C3 is not enough).
    if (!OrdreSchema.safeParse(ordre).success || !UUID_RE.test(id)) {
      console.warn(
        '[DecisionViewerService] ordre/id invalide, ouverture refusée',
        { ordre, id },
      );
      return;
    }
    const key = usePreviewPanelStore.getState().openDecision({
      ordre,
      id,
      caseId,
      title: sanitizeDecisionTabTitle(title),
    });
    if (!key) {
      // Même plafond, même message que côté pièces : les onglets sont comptés
      // ensemble, donc le refus doit se lire pareil quel que soit ce qu'on ouvrait.
      toast.info('Limite de 10 aperçus atteinte', {
        description: 'Fermez un onglet avant d’ouvrir un autre arrêt.',
      });
    }
  },
};
